const TOKEN_NAME = '_wc';
const SESSION_UUID_NAME = '_ws';
const XOR_KEY = process.env.REACT_APP_STORAGE_KEY as string;

/**
 * XOR cipher - applies XOR operation between value and key.
 * Running it twice with the same key returns the original value.
 */
const xorCipher = (value: string, key: string): string => {
  let result = '';
  for (let i = 0; i < value.length; i++) {
    result += String.fromCharCode(value.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
};

/**
 * Obfuscate a value using XOR cipher + Base64 encoding.
 * This prevents casual reading of sensitive values in storage.
 */
const obfuscate = (value: string): string => {
  if (!value) return '';
  try {
    const xored = xorCipher(value, XOR_KEY);
    return btoa(xored);
  } catch {
    return value;
  }
};

/**
 * Deobfuscate a value that was obfuscated with the obfuscate function.
 */
const deobfuscate = (value: string): string => {
  if (!value) return '';
  try {
    const xored = atob(value);
    return xorCipher(xored, XOR_KEY);
  } catch {
    return '';
  }
};

export const setTokenLocalStorage = (token?: string) => {
  if (token) {
    window.localStorage.setItem(TOKEN_NAME, obfuscate(token));
  }
};

export const setTokenSessionStorage = (token?: string) => {
  if (token) {
    window.sessionStorage.setItem(TOKEN_NAME, obfuscate(token));
  }
};

export const getTokenLocalStorage = () => {
  const value = window.localStorage.getItem(TOKEN_NAME) || '';
  return deobfuscate(value);
};

export const getTokenSessionStorage = () => {
  const value = window.sessionStorage.getItem(TOKEN_NAME) || '';
  return deobfuscate(value);
};

export const removeToken = () => {
  window.sessionStorage.removeItem(TOKEN_NAME);
  window.localStorage.removeItem(TOKEN_NAME);
};

export const removeSessionUuid = () => {
  window.sessionStorage.removeItem(SESSION_UUID_NAME);
  window.localStorage.removeItem(SESSION_UUID_NAME);
};

export const setSessionUuidLocalStorage = (uuid: string) => {
  window.localStorage.setItem(SESSION_UUID_NAME, obfuscate(uuid));
};

export const setSessionUuidSessionStorage = (uuid: string) => {
  window.sessionStorage.setItem(SESSION_UUID_NAME, obfuscate(uuid));
};

export const getSessionUuid = () => {
  if (getTokenSessionStorage()) {
    const value = window.sessionStorage.getItem(SESSION_UUID_NAME) || '';
    return deobfuscate(value);
  }
  if (getTokenLocalStorage()) {
    const value = window.localStorage.getItem(SESSION_UUID_NAME) || '';
    return deobfuscate(value);
  }
  return '';
};
